import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Renew, Search } from "@carbon/icons-react";
import {
    Tabs, TabList, Tab, TabPanels, TabPanel,
    Grid, Column, Stack, ComboBox, Button,
    DatePicker, DatePickerInput
} from "@carbon/react";
import Card from "../../../components/Layouts/Card";
import SearchTable from "../../../components/Basics/SearchTable.container";
import { onGetTrucksThunk, onGetCurrentCyclesThunk, onGetHistoryCyclesThunk } from "./Cycles.actions";
import LoadingContent from "../../../components/Animations/LoadingContent";


const CycleContainer = () => {
    const dispatch = useDispatch();
    const { data: trucks, current, history } = useSelector(state => state.cycles);
    const loadingCurrent = current.loading;
    const loadingHistory = history.loading;
    const errorCurrent = current.error;
    const errorHistory = history.error;

    useEffect(() => {
        dispatch(onGetTrucksThunk());
        dispatch(onGetCurrentCyclesThunk()); // turno actual al montar
    }, [dispatch]);

    const ALL_OPTION = { id: "ALL", label: "Todos los equipos" };
    const [equipo, setEquipo] = useState(ALL_OPTION);
    const [fecha, setFecha] = useState(null);
    const [turno, setTurno] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // equipos desde trucks
    const equipos = useMemo(() => {
        if (!Array.isArray(trucks)) return [ALL_OPTION];
        const seen = new Set();
        const result = [ALL_OPTION];
        for (const r of trucks) {
            const rawId = r?.device_id ?? r?.deviceId ?? r?.device ?? r?.imei;
            if (!rawId) continue;
            const id = String(rawId);
            if (id === "863719060429827") continue;
            if (seen.has(id)) continue;
            seen.add(id);
            const label = r?.patente ? String(r.patente) : id;
            result.push({ id, label });
        }
        return result;
    }, [trucks]);

    const turnos = [
        { id: "dia", label: "Día" },
        { id: "noche", label: "Noche" },
    ];

    const formatDec = (v, digits) => {
    if (v === null || v === undefined || v === "") return "";
    const num = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (!isNaN(num)) return num.toFixed(digits).replace(".", ",");
    // si no es numérico, al menos reemplazamos puntos por comas
    return String(v).replace(/\./g, ",");
    };

    const fmt2 = (v) => formatDec(v, 2);
    const fmt3 = (v) => formatDec(v, 3);

    const mapRow = useCallback((c) => ({
        id: c.id,
        patente: c.patente,
        marca: c.marca,
        modelo: c.modelo,
        ciclo: c.ciclo,
        primer_timestamp: c.primer_timestamp ? new Date(c.primer_timestamp).toLocaleString("es-CL") : "",
        ultimo_timestamp: c.ultimo_timestamp ? new Date(c.ultimo_timestamp).toLocaleString("es-CL") : "",
        primer_total_odometer: c.primer_total_odometer ?? "",
        ultimo_total_odometer: c.ultimo_total_odometer ?? "",
        primer_fuel_consumed_total: c.primer_fuel_consumed_total ?? "",
        ultimo_fuel_consumed_total: c.ultimo_fuel_consumed_total ?? c.ultimo_fuel_consumido ?? "",
        geocerca_origen: c.geocerca_origen ?? "",
        geocerca_destino: c.geocerca_destino ?? "",
        // ⬇️ formateadas con coma
        duracion_ciclo_minutos: fmt2(c.duracion_ciclo_minutos),
        km_recorridos: fmt3(c.km_recorridos),
        combustible_consumido: fmt2(c.combustible_consumido) ?? "",
        primer_engine_worktime_counted: c.primer_engine_worktime_counted ?? "",
        ultimo_engine_worktime_counted: c.ultimo_engine_worktime_counted ?? "",
        diferencia_horometro: fmt2(c.diferencia_horometro),
        loading_gain_kg_inicio: typeof c.loading_gain_kg_inicio === "number" ? c.loading_gain_kg_inicio.toFixed(0) : (c.loading_gain_kg_inicio ?? "")
    }), []);



    const filterByEquipo = useCallback((list) => {
        if (!Array.isArray(list)) return [];
        const isAll = !equipo || equipo.id === "ALL";
        if (isAll) return list.map(mapRow);
        const selectedPatente = String(equipo.label).trim().toUpperCase();
        return list
            .filter(c => (c?.patente ? String(c.patente).trim().toUpperCase() : "") === selectedPatente)
            .map(mapRow);
    }, [equipo, mapRow]);

    const rowsCurrent = useMemo(() => filterByEquipo(current.data), [current.data, filterByEquipo]);
    const rowsHistory = useMemo(() => filterByEquipo(history.data), [history.data, filterByEquipo]);

    const headers = [
        { key: 'patente', header: 'Patente' },
        { key: 'ciclo', header: 'Ciclo' },
        { key: 'primer_timestamp', header: 'Hora inicio' },
        { key: 'ultimo_timestamp', header: 'Hora fin' },
        { key: 'primer_total_odometer', header: 'Odómetro inicial (m)' },
        { key: 'ultimo_total_odometer', header: 'Odómetro final (m)' },
        { key: 'primer_engine_worktime_counted', header: 'Horómetro inicial (min)' },
        { key: 'ultimo_engine_worktime_counted', header: 'Horómetro final (min)' },
        { key: 'geocerca_origen', header: 'Geocerca origen' },
        { key: 'geocerca_destino', header: 'Geocerca destino' },
        { key: 'loading_gain_kg_inicio', header: 'Carga aprox (kg)' },
        { key: 'duracion_ciclo_minutos', header: 'Duración ciclo (min)' },
        { key: 'diferencia_horometro', header: 'Diferencia horómetro (min)' },
        { key: 'km_recorridos', header: 'Km recorridos' },
        { key: 'combustible_consumido', header: 'Combustible consumido (L)' },
    ];

    const handleRefrescarTurnoActual = useCallback(() => {
        setActiveTab(0);
        dispatch(onGetCurrentCyclesThunk());
    }, [dispatch]);

    const handleBuscarHistorico = useCallback(() => {
        if (!fecha || !turno) return;
        setActiveTab(1);
        dispatch(onGetHistoryCyclesThunk(fecha, turno));
    }, [dispatch, fecha, turno]);

    const [openExcelModal, setOpenExcelModal] = useState(false);
    const [programaInterplanta, setProgramaInterplanta] = useState(null); // <- aquí queda el array de objetos

    const handleExcelReady = useCallback((records, meta) => {
        // Aquí recibes el "diccionario" listo para cálculos
        // records: Array<Object>, meta: { headers, rows, fileName }
        setProgramaInterplanta(records);

        // EJEMPLO: dejarlo accesible en consola / o disparar un cálculo:
        console.log("Programa Interplanta listo:", meta, records.slice(0, 3));
        // -> aquí puedes invocar alguna acción o cálculo con "records"
    }, []);

    const FormTurnoActual = () => (
        <Card $justifyContent="flex-start" background="transparent" flexDirection="column" alignItems="stretch">
            <Grid condensed fullWidth style={{ width: "100%", columnGap: "2rem" }}>
                <Column lg={6} md={6} sm={12} style={{ paddingRight: "1rem" }}>
                    <ComboBox
                        id="equipos-actual"
                        size="md"
                        items={equipos}
                        itemToString={(item) => (item ? item.label : "")}
                        titleText="Equipo"
                        placeholder="Todos los equipos"
                        selectedItem={equipo ?? ALL_OPTION}
                        onChange={({ selectedItem }) => setEquipo(selectedItem || ALL_OPTION)}
                    />
                    <p style={{ color: "#8D8D8D", fontSize: "0.8rem", marginTop: "0.5rem" }}>
                        Filtra los ciclos del turno en curso por patente.
                    </p>
                </Column>

                <Column
                    lg={4}
                    md={4}
                    sm={12}
                    style={{ alignSelf: 'center' }}   // <-- centra verticalmente en la fila
                >
                    <Button
                        size="md"
                        kind="primary"
                        renderIcon={Renew}
                        onClick={handleRefrescarTurnoActual}
                        disabled={loadingCurrent}
                    >
                        Actualizar turno actual
                    </Button>
                </Column>
            </Grid>
        </Card >
    );

    const FormHistoricos = () => (
        <Card $justifyContent="flex-start" background="transparent" flexDirection="column" alignItems="stretch">
            <Grid condensed fullWidth style={{ width: "100%", columnGap: "2rem" }}>
                <Column lg={4} md={4} sm={12} style={{ paddingRight: "1rem" }}>
                    <ComboBox
                        id="equipos-historico"
                        size="md"
                        items={equipos}
                        itemToString={(item) => (item ? item.label : "")}
                        titleText="Equipo"
                        placeholder="Todos los equipos"
                        selectedItem={equipo ?? ALL_OPTION}
                        onChange={({ selectedItem }) => setEquipo(selectedItem || ALL_OPTION)}
                    />
                    <p style={{ color: "#8D8D8D", fontSize: "0.8rem", marginTop: "0.5rem" }}>
                        Acá se pueden ver los ciclos de uno o más equipos a la vez.
                    </p>
                </Column>

                <Column lg={4} md={4} sm={12}>
                    <DatePicker
                        datePickerType="single"
                        dateFormat="d-m-Y"
                        maxDate={new Date()}
                        value={fecha ? [fecha] : []}
                        onChange={(dates) => setFecha(dates?.[0] ?? null)}
                    >
                        <DatePickerInput id="fecha-ciclos-hoy" size="lg" labelText="Fecha de los ciclos" placeholder="dd-mm-yyyy" />
                    </DatePicker>
                </Column>

                <Column lg={4} md={4} sm={12}>
                    <ComboBox
                        id="turnos-hoy"
                        size="md"
                        items={turnos}
                        itemToString={(item) => (item ? item.label : "")}
                        titleText="Turno de los Ciclos"
                        placeholder="Seleccione un tipo de turno"
                        onChange={({ selectedItem }) => setTurno(selectedItem?.id ?? null)}
                        selectedItem={turnos.find(t => t.id === turno) || null}
                    />
                    <p style={{ color: "#8D8D8D", fontSize: "0.8rem", marginTop: "0.5rem" }}>
                        Seleccione el turno al que corresponden los ciclos.
                    </p>
                </Column>

                <Column
                    lg={4}
                    md={4}
                    sm={12}
                    style={{ alignSelf: 'center' }}   // <-- centra verticalmente en la fila
                >
                    <Button
                        size="md"
                        kind="primary"
                        renderIcon={Search}
                        onClick={handleBuscarHistorico}
                        disabled={!fecha || !turno || loadingHistory}
                    >
                        Buscar Ciclos
                    </Button>
                </Column>
            </Grid>
        </Card>
    );

    return (
        <>
            {(errorCurrent || errorHistory) && (
                <Card width="20%">
                    <p style={{ color: "#8D8D8D" }}>
                        {errorCurrent ? "Error cargando turno actual" : "Error cargando históricos"}
                    </p>
                </Card>
            )}

            <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex)}>
                <TabList activation="manual" aria-label="List of tabs">
                    <Tab>Turno actual</Tab>
                    <Tab>Reportes anteriores</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel style={{ width: "100%" }}>
                        <Stack gap={3}>
                            <FormTurnoActual />
                            {loadingCurrent ? <LoadingContent /> : <SearchTable rows={rowsCurrent} headers={headers} />}
                        </Stack>
                    </TabPanel>

                    <TabPanel style={{ width: "100%" }}>
                        <Stack gap={3}>
                            <FormHistoricos />
                            {loadingHistory ? <LoadingContent /> : <SearchTable rows={rowsHistory} headers={headers} />}
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </>
    );
};

export default CycleContainer;